// Guided tutorial — overlay that highlights pieces of the UI and walks the
// user through the admin workflow. Uses a CSS spotlight cutout around the
// target element, plus a floating tooltip card.
import React from 'react';
import { Icon } from './ui.jsx';

// A tutorial step targets an element by a `data-tutorial` attribute.
// Sprinkling those into the UI is the only wiring needed on each screen.
const STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to Commissions by Staffology',
    body: "60-second tour. We'll walk through the main pieces — you can skip any step. Let's start with the sidebar.",
    target: null, // centered intro
  },
  {
    id: 'dashboard',
    title: 'Dashboard — your home base',
    body: 'At a glance: current run status, last finalized commission, unassigned clients, open AR. Click any card to jump in.',
    target: '[data-tutorial="sidebar-dashboard"]',
    nav: 's02',
  },
  {
    id: 'runs',
    title: 'Commission Runs',
    body: 'Every month (or custom period) is a "run". From here you create a new one, upload your Transaction + AR files, and see the history.',
    target: '[data-tutorial="sidebar-runs"]',
    nav: 's03',
  },
  {
    id: 'customers',
    title: 'Customers',
    body: 'Every client that has ever appeared in a run. You can reassign the sales rep for any customer here — it persists across runs.',
    target: '[data-tutorial="sidebar-customers"]',
    nav: 's11',
  },
  {
    id: 'users',
    title: 'Users — invite your team',
    body: 'Add sales reps and other admins. Reps see only their own commission. Admins can override, approve, and finalize runs.',
    target: '[data-tutorial="sidebar-users"]',
    nav: 's22',
  },
  {
    id: 'rates',
    title: 'Rates',
    body: 'Tenant-wide defaults for ACA, SICK, Y1 and Y2+. Each rep can override their own Y1/Y2+ in Users. Changes apply to new runs only.',
    target: '[data-tutorial="sidebar-rates"]',
    nav: 's14',
  },
  {
    id: 'tenant-picker',
    title: 'Tenant switcher',
    body: "If you run commissions for more than one entity, create a new tenant here — each with its own rate table and column mapping.",
    target: '[data-tutorial="tenant-picker"]',
  },
  {
    id: 'approval-flow',
    title: 'Approval flow: Draft → Finalized → Approved',
    body: "You create and calculate in Draft. Admin locks numbers via Finalize. A different admin reviews and Approves — then reps see their final commission.",
    target: null,
  },
  {
    id: 'done',
    title: "You're set",
    body: 'Help is always in the top-right. Re-take this tour any time.',
    target: null,
  },
];

export function Tutorial({ open, onClose, onNav }) {
  const [i, setI] = React.useState(0);
  const [rect, setRect] = React.useState(null);

  // Reset to the first step when re-opened.
  React.useEffect(() => { if (open) setI(0); }, [open]);

  const step = STEPS[i];
  const total = STEPS.length;

  // Auto-navigate to the step's screen (so the highlighted element is on-page).
  React.useEffect(() => {
    if (!open || !step) return;
    if (step.nav && onNav) onNav(step.nav);
  }, [i, open]);

  // Measure the target element whenever the step changes or the window resizes.
  React.useEffect(() => {
    if (!open) return;
    const measure = () => {
      if (!step?.target) { setRect(null); return; }
      // Give the UI a frame to update if we just navigated.
      requestAnimationFrame(() => {
        const el = document.querySelector(step.target);
        if (!el) { setRect(null); return; }
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      });
    };
    const t = setTimeout(measure, 60);
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => { clearTimeout(t); window.removeEventListener('resize', measure); window.removeEventListener('scroll', measure, true); };
  }, [i, open, step?.target]);

  if (!open) return null;

  // Backdrop with a rounded-square hole where the target is.
  const pad = 8;
  const spotlight = rect
    ? { top: rect.top - pad, left: rect.left - pad, width: rect.width + pad * 2, height: rect.height + pad * 2 }
    : null;

  // Tooltip placement: prefer to the right of spotlight; else below; else centered.
  let tip = null;
  if (spotlight) {
    const rightOf = spotlight.left + spotlight.width + 12;
    const fitsRight = rightOf + 380 < window.innerWidth;
    if (fitsRight) {
      tip = { top: Math.max(24, spotlight.top), left: rightOf };
    } else {
      tip = { top: spotlight.top + spotlight.height + 12, left: Math.max(24, Math.min(spotlight.left, window.innerWidth - 404)) };
    }
  }

  return (
    <div className="fixed inset-0 z-[70]" aria-live="polite">
      {/* Backdrop — using inset shadow so the spotlight cutout reads cleanly. */}
      {spotlight ? (
        <>
          <div className="fixed inset-0 bg-neutral-900/55 pointer-events-auto" onClick={onClose}/>
          <div
            className="absolute rounded-xl pointer-events-none transition-all duration-200"
            style={{
              ...spotlight,
              boxShadow: '0 0 0 9999px rgba(17,20,24,0.55), 0 0 0 2px rgba(249,122,78,0.9), 0 0 24px rgba(249,122,78,0.4)',
            }}
          />
        </>
      ) : (
        <div className="fixed inset-0 bg-neutral-900/60 flex items-center justify-center pointer-events-auto" onClick={onClose}/>
      )}

      {/* Tooltip card */}
      <div
        className={`fixed w-[380px] bg-white rounded-xl shadow-2xl border border-neutral-200 p-5 pointer-events-auto ${!tip ? 'left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2' : ''}`}
        style={tip ? { top: tip.top, left: tip.left } : {}}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="inline-flex items-center gap-2 px-2 py-0.5 rounded-full bg-primary-100 text-primary-900 text-[10px] font-bold uppercase tracking-wider mb-2">
              Step {i + 1} of {total}
            </div>
            <div className="text-[16px] font-bold text-neutral-900 tracking-[-0.01em]">{step.title}</div>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-700 -mr-1 -mt-1">
            <Icon name="close" size={16}/>
          </button>
        </div>

        <div className="text-[13.5px] text-neutral-700 leading-relaxed mt-3">{step.body}</div>

        {/* Progress dots */}
        <div className="flex items-center gap-1 mt-5">
          {STEPS.map((_, idx) => (
            <button key={idx} onClick={() => setI(idx)}
              className={`h-1.5 rounded-full transition-all ${idx === i ? 'w-6 bg-primary-700' : 'w-1.5 bg-neutral-200 hover:bg-neutral-300'}`}
              aria-label={`Go to step ${idx + 1}`}/>
          ))}
        </div>

        <div className="mt-5 flex items-center justify-between">
          <button onClick={onClose} className="text-[12px] text-neutral-500 hover:text-neutral-800 font-medium">Skip tour</button>
          <div className="flex items-center gap-2">
            {i > 0 && (
              <button onClick={() => setI(i - 1)}
                className="h-9 px-3 rounded-md border border-neutral-200 bg-white text-[13px] font-medium text-neutral-700 hover:border-neutral-300">
                Back
              </button>
            )}
            {i < total - 1 ? (
              <button onClick={() => setI(i + 1)}
                className="h-9 px-4 rounded-md bg-neutral-900 hover:bg-black text-white text-[13px] font-semibold inline-flex items-center gap-1.5">
                Next
                <Icon name="arrow" size={13}/>
              </button>
            ) : (
              <button onClick={onClose}
                className="h-9 px-4 rounded-md bg-primary-700 hover:bg-primary-900 text-white text-[13px] font-semibold">
                Finish
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
